/**
 * Wire-mapping for the CAE (Continuous Access Evaluation) family (OIDCPAM
 * Gate-6 Phase 5e — CAEDashboard.tsx).
 *
 * AdminCAEEventResp / AdminCAERiskStateResp are concretely typed by the
 * generator (see gen/client/react/client.ts) and the pre-migration page's
 * genEventToSecurityEvent/genRiskToRiskState already read the correct
 * camelCase fields off them field-by-field. Verified against the real client
 * types; NO latent snake_case/camelCase bug found for events/risk state
 * (same "clean" class as the Phase-5a AccessRequests / Phase-5d
 * EphemeralCreds migrations).
 *
 * The one field that IS opaque is AdminCAEConfigResp.thresholds
 * (Record<string, unknown>): admin_cae.go#AdminGetCAEConfig writes the Go
 * `RiskThresholds` struct directly (pam_risk_scorer.go — its fields have NO
 * json tags), so the wire keys are the Go field names VERBATIM
 * (PascalCase: ImpossibleTravelKmCritical, FailedAuthBurstHigh, ...). The
 * generated client's fromWire() snake_case->camelCase boundary mapper
 * (client.ts's blind convertKeys(value, snakeToCamel)) is a no-op on keys
 * with no underscores, so these PascalCase keys pass through UNCHANGED —
 * this is NOT the snake_case/camelCase latent-bug class (there's no
 * underscore to mis-convert), and the pre-migration page's numField() helper
 * already read these PascalCase keys correctly. This mapping preserves that
 * same PascalCase read while translating to @devnw/cyber-iam/iam/types'
 * camelCase RiskThresholds prop shape.
 */
import type {
  AdminCAEEventResp,
  AdminCAERiskStateResp,
} from '@gen/react/client'
import type {
  SecurityEvent as IamSecurityEvent,
  RiskState as IamRiskState,
  RiskThresholds as IamRiskThresholds,
} from '@devnw/cyber-iam/iam/types'

/** HOST-COMPUTED display formatting — the library never parses dates. */
function fmtDate(iso?: string): string | undefined {
  return iso ? new Date(iso).toLocaleString() : undefined
}

export function toIamSecurityEvent(ev: AdminCAEEventResp): IamSecurityEvent {
  return {
    id: ev.id,
    eventType: ev.eventType,
    severity: ev.severity,
    subjectRef: ev.subjectRef,
    source: ev.source,
    sessionId: ev.sessionId,
    occurredAt: fmtDate(ev.occurredAt),
  }
}

export function toIamRiskState(s: AdminCAERiskStateResp): IamRiskState {
  return {
    subjectRef: s.subjectRef,
    currentLevel: s.currentLevel,
    contributingIds: s.contributingIds ?? [],
    sessionId: s.sessionId,
    computedAt: fmtDate(s.computedAt),
  }
}

/** Reads a numeric field off the opaque PascalCase thresholds record. */
function numField(rec: Record<string, unknown>, key: string): number {
  const v = rec[key]
  return typeof v === 'number' ? v : 0
}

export function toIamRiskThresholds(
  rec: Record<string, unknown>
): IamRiskThresholds {
  return {
    impossibleTravelKmCritical: numField(rec, 'ImpossibleTravelKmCritical'),
    failedAuthBurstHigh: numField(rec, 'FailedAuthBurstHigh'),
    failedAuthBurstCritical: numField(rec, 'FailedAuthBurstCritical'),
    velocityAnomalyHigh: numField(rec, 'VelocityAnomalyHigh'),
    velocityAnomalyCritical: numField(rec, 'VelocityAnomalyCritical'),
    sensitiveActionRateHigh: numField(rec, 'SensitiveActionRateHigh'),
  }
}
